import React, { useEffect, useCallback, ReactNode } from 'react';

const DEFAULT_TIME = 1000;

type DelayFunction = () => void;

const delay = (fn: DelayFunction, time: number = DEFAULT_TIME): NodeJS.Timeout => {
  const timeoutId = setTimeout(() => fn(), time);
  return timeoutId;
};

export const STATUSES = {
  REQUEST: 'request',
  SUCCESS: 'success',
  REJECT: 'reject',
} as const;

export type StatusType = keyof typeof STATUSES | string;

interface TimeWorkerProps {
  children?: ReactNode;
  action: () => void;
  time?: number;
  onReject?: () => void;
  onSuccess?: () => void;
  status?: StatusType;
}

export const TimeWorker = (props: TimeWorkerProps) => {
  const handleRef = React.useRef<NodeJS.Timeout | null>(null);

  const stop = useCallback(() => {
    if (handleRef.current) {
      clearTimeout(handleRef.current);
      handleRef.current = null;
    }
  }, []);

  const nextTick = useCallback(() => {
    stop();
    handleRef.current = delay(props.action, props.time);
  }, [props.action, props.time, stop]);

  const success = useCallback(() => {
    nextTick();
    props.onSuccess?.();
  }, [nextTick, props.onSuccess]);

  const reject = useCallback(() => {
    stop();
    props.onReject?.();
  }, [stop, props.onReject]);

  useEffect(() => {
    if (props.status === STATUSES.SUCCESS) {
      success();
    } else if (props.status === STATUSES.REJECT) {
      reject();
    }
  }, [status, success, reject]);

  useEffect(() => {
    nextTick();
    return () => stop();
  }, [nextTick, stop]);

  return <>{props.children}</>;
};

TimeWorker.STATUSES = STATUSES;
